import { apiClient } from "@/services/api";
import { logError, ApiError } from "@/lib/error-handling";
import { ActivitySentimentDetailDto, ContactSentimentEventDto } from "@/types";

/**
 * Fetches the per-activity sentiment events for a contact (SKR-703/719 contract).
 * The endpoint responds with `{ events }` in descending chronological order;
 * returns `[]` when the key is absent so callers can render straight to a list.
 */
export async function fetchContactSentimentEvents(
  contactId: string
): Promise<ContactSentimentEventDto[]> {
  try {
    const json = await apiClient<{ events?: ContactSentimentEventDto[] }>(
      `/api/contacts/${contactId}/sentiment-events`
    );
    return json.events ?? [];
  } catch (error) {
    // A 404 means "no sentiment data for this contact yet" — degrade to an
    // empty list so the profile renders its empty state instead of erroring.
    if (error instanceof ApiError && error.status === 404) return [];
    logError(error, "fetchContactSentimentEvents");
    throw error;
  }
}

/**
 * Fetches an activity's full content plus its activity-level sentiment and the
 * highlight spans to wrap, for the activity-detail modal (SKR-725).
 *
 * `contactId` is optional but selects which highlights come back (SKR-832), not
 * just the header name: spans belonging to another attendee are dropped and the
 * rest are scoped "contact" or "activity". Attribution has no fallback, so
 * omitting it returns the activity-level read with no "contact" spans at all.
 */
export async function fetchActivitySentimentDetail(
  activityId: string,
  contactId?: string
): Promise<ActivitySentimentDetailDto> {
  try {
    const query = contactId
      ? `?contactId=${encodeURIComponent(contactId)}`
      : "";
    return await apiClient<ActivitySentimentDetailDto>(
      `/api/activities/${activityId}/sentiment-detail${query}`
    );
  } catch (error) {
    logError(error, "fetchActivitySentimentDetail");
    throw error;
  }
}
